import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MONTHS_LOCAL } from "../../../../types/IMonth";
import { getDefaultSelectedMonths, sortMonthsByTimeline } from "../FavoriteOriginHelper";

const MONTHS = MONTHS_LOCAL;
const WINDOW_VISIBLE_COUNT = 12;
const MONTH_WINDOW_SCROLL_STEP_FALLBACK = 168;
// Persisted (not just in-memory) so a rollover is still detected after closing and reopening the app.
const SELECTED_MONTHS_GLOBAL_KEY = "nitro.originManager.selectedMonths";
const LAST_SEEN_MONTH_GLOBAL_KEY = "nitro.originManager.lastSeenMonth";

export interface UseOriginManagerMonthsResult {
  currentMonth: number;
  visibleMonths: string[];
  selectedMonths: string[];
  toggleMonth: (month: string) => void;
  getMonthLabel: (month: string) => string;
  monthWindowScrollRef: React.RefObject<HTMLDivElement>;
  canScrollMonthsBack: boolean;
  canScrollMonthsForward: boolean;
  updateMonthWindowScrollState: () => void;
  handleScrollMonthsBack: () => void;
  handleScrollMonthsForward: () => void;
}

// Manages the 12-month visible window, the user's month selection (incl.
// localStorage hydration/persistence), and the month-chip scroll affordances.
export const useOriginManagerMonths = (): UseOriginManagerMonthsResult => {
  // MONTH LOGIC (0-indexed so Jan=0)
  const currentMonth = new Date().getMonth();

  const getMonthLabel = useCallback(
    (month: string) => {
      const monthIndex = MONTHS.indexOf(month);
      const year =
        monthIndex >= currentMonth
          ? new Date().getFullYear()
          : new Date().getFullYear() + 1;
      return `${month} ${String(year).slice(-2)}`;
    },
    [currentMonth],
  );

  // Fixed 12-month window anchored at the current month.
  const visibleMonths = useMemo(
    () =>
      Array.from(
        { length: WINDOW_VISIBLE_COUNT },
        (_, i) => MONTHS[(currentMonth + i) % 12],
      ),
    [currentMonth],
  );

  // default the first 3 months from the current month on initial load
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() =>
    getDefaultSelectedMonths(currentMonth),
  );
  const [hasHydratedSelectedMonths, setHasHydratedSelectedMonths] =
    useState(false);

  const monthWindowScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollMonthsBack, setCanScrollMonthsBack] = useState(false);
  const [canScrollMonthsForward, setCanScrollMonthsForward] = useState(false);

  // Get user's month selections from local storage
  useEffect(() => {
    const defaultMonths = getDefaultSelectedMonths(currentMonth);

    // If running on the server, use default months
    if (typeof window === "undefined") {
      setHasHydratedSelectedMonths(true);
      return;
    }

    // Read the month we last recorded (persisted, so it survives closing/reopening the app).
    const rawLastSeenMonth = window.localStorage.getItem(
      LAST_SEEN_MONTH_GLOBAL_KEY,
    );
    const parsedLastSeenMonth = rawLastSeenMonth
      ? Number.parseInt(rawLastSeenMonth, 10)
      : NaN;
    const previousMonth =
      Number.isInteger(parsedLastSeenMonth) &&
      parsedLastSeenMonth >= 0 &&
      parsedLastSeenMonth < 12
        ? parsedLastSeenMonth
        : currentMonth;
    const monthsElapsed = (currentMonth - previousMonth + 12) % 12;

    // Drops any month that rolled behind the current month, so it isn't kept selected as "next year".
    const pruneStaleMonths = (months: string[]) =>
      months.filter(
        (month) =>
          (MONTHS.indexOf(month) - previousMonth + 12) % 12 >= monthsElapsed,
      );

    const raw = window.localStorage.getItem(SELECTED_MONTHS_GLOBAL_KEY);

    // If no selected months are stored, use default months
    if (!raw) {
      setSelectedMonths(defaultMonths);
      window.localStorage.setItem(
        LAST_SEEN_MONTH_GLOBAL_KEY,
        String(currentMonth),
      );
      setHasHydratedSelectedMonths(true);
      return;
    }

    // Parse the stored selected months
    try {
      const parsed = JSON.parse(raw) as string[];
      const normalized = pruneStaleMonths(
        parsed.filter((month) => MONTHS.includes(month)),
      );
      const deduped = sortMonthsByTimeline(normalized, currentMonth);

      setSelectedMonths(deduped.length > 0 ? deduped : defaultMonths);
    } catch {
      setSelectedMonths(defaultMonths);
    }

    window.localStorage.setItem(
      LAST_SEEN_MONTH_GLOBAL_KEY,
      String(currentMonth),
    );
    setHasHydratedSelectedMonths(true);
  }, [currentMonth]);

  // Persist user's month selections to local storage
  useEffect(() => {
    if (!hasHydratedSelectedMonths || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(
      SELECTED_MONTHS_GLOBAL_KEY,
      JSON.stringify(selectedMonths),
    );
  }, [selectedMonths, hasHydratedSelectedMonths]);

  // Toggle a month in the selectedMonths state.
  const toggleMonth = useCallback(
    (month: string) => {
      setSelectedMonths((prev) => {
        // If the month is already selected, remove it from the array.
        if (prev.includes(month)) {
          // Ensure at least one month remains selected; if only one month is selected, do not remove it.
          if (prev.length === 1) {
            return prev;
          }
          return prev.filter((item) => item !== month);
        }

        // If the month is not already selected, add it and sort the array by timeline.
        return sortMonthsByTimeline([...prev, month], currentMonth);
      });
    },
    [currentMonth],
  );

  const updateMonthWindowScrollState = useCallback(() => {
    const container = monthWindowScrollRef.current;
    if (!container) {
      setCanScrollMonthsBack(false);
      setCanScrollMonthsForward(false);
      return;
    }

    const maxScrollLeft = Math.max(
      0,
      container.scrollWidth - container.clientWidth,
    );
    setCanScrollMonthsBack(container.scrollLeft > 1);
    setCanScrollMonthsForward(container.scrollLeft < maxScrollLeft - 1);
  }, []);

  const handleScrollMonthsBack = useCallback(() => {
    const container = monthWindowScrollRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({ left: -MONTH_WINDOW_SCROLL_STEP_FALLBACK, behavior: "smooth" });
  }, []);

  const getMonthWindowScrollStep = useCallback(() => {
    const container = monthWindowScrollRef.current;
    if (!container || typeof window === "undefined") {
      return MONTH_WINDOW_SCROLL_STEP_FALLBACK;
    }

    const chip = container.querySelector<HTMLButtonElement>(".dom-chip");
    if (!chip) {
      return MONTH_WINDOW_SCROLL_STEP_FALLBACK;
    }

    const styles = window.getComputedStyle(container);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");
    return Math.max(1, chip.getBoundingClientRect().width + (Number.isFinite(gap) ? gap : 0));
  }, []);

  const handleScrollMonthsForward = useCallback(() => {
    const container = monthWindowScrollRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({ left: getMonthWindowScrollStep(), behavior: "smooth" });
  }, [getMonthWindowScrollStep]);

  useEffect(() => {
    updateMonthWindowScrollState();
  }, [updateMonthWindowScrollState, visibleMonths]);

  useEffect(() => {
    const handleResize = () => {
      updateMonthWindowScrollState();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateMonthWindowScrollState]);

  return {
    currentMonth,
    visibleMonths,
    selectedMonths,
    toggleMonth,
    getMonthLabel,
    monthWindowScrollRef,
    canScrollMonthsBack,
    canScrollMonthsForward,
    updateMonthWindowScrollState,
    handleScrollMonthsBack,
    handleScrollMonthsForward,
  };
};
